import getIn from 'lodash/get';
import noop from 'lodash/noop';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { call, delay, put, select } from 'redux-saga/effects';

import * as PlaybackConstants from '../../constants';
import * as AudioActions from '../actions';
import * as AudioConstants from '../constants';
import * as D from '../deps';
import * as AudioUtils from '../utils';

const log = D.Logger.child({ component: 'annex/playback' });

const resetBufferErrors = [AudioConstants.CommonAudioErrorCodes.DECODE];

function* reportAudioErrorForAnalytics(action) {
  const baseData = yield select(getBaseAudioEventData, action, 'error');
  yield put(D.getInDomain('analytics.actions.createAudioTrackPlaybackEvent', noop)(baseData));
}

export function* handleNotifyTooManyPlaybackErrors() {
  yield put(
    D.Actions.Modal.openModal({
      body: <FormattedMessage {...PlaybackConstants.PlaybackMessages.blockedAudioErrorBody} />,
      buttons: [
        <D.Button key={0} onClick={() => location.reload()}>
          <FormattedMessage {...PlaybackConstants.PlaybackMessages.blockedAudioErrorRefresh} />
        </D.Button>,
        <D.Button key={1} onClick={() => (window.location = D.Utils.Route.getHelpUrl())}>
          <FormattedMessage {...PlaybackConstants.PlaybackMessages.blockedAudioErrorSupport} />
        </D.Button>,
      ],
      hideXRow: true,
      modalType: 'ModalStandard',
      title: <FormattedMessage {...PlaybackConstants.PlaybackMessages.blockedAudioErrorTitle} />,
    }),
  );
}

export function* handlePlaybackError(action) {
  const { payload } = action;
  const { token, harnessName, ERROR_OBJ: error, url } = payload;
  const sourceId = yield select(D.getInDomain('playback.utils.getCurrentPlaylist'));
  const notSupportedSrcErrorCount = yield select(AudioUtils.getNumberOfSrcNotSupportedErrors);
  const decodeErrorCount = yield select(AudioUtils.getNumberOfDecodeErrors);
  const networkErrorCount = yield select(AudioUtils.getNumberOfNetworkErrors);
  const currentTrackTime = yield select(AudioUtils.getCurrentTrackTime);
  const trackModelType = yield select(
    D.getInDomain('playback.utils.getCurrentPlaylistTrackModelType'),
  );
  const player = AudioUtils.AudioLoader.getPlayer();

  // When an error occurs, if the track has been played, its royalty must be paid.
  // Otherwise, a credit request can be sent out. In scenarios that an error occurs
  // before the track is logged into drm, if such scenario exists, it is safe to
  // send the credit request, since the drm reconciliation will filter out such
  // "orphaned" requests.
  if (currentTrackTime === 0 && trackModelType !== 'APSItem') {
    yield call(D.putAndWait, {
      type: D.Constants.Playback.ActionTypes.LOG_DRM_RECEIPT,
    });
  }

  yield call(reportAudioErrorForAnalytics, action);

  if (error.commonType === AudioConstants.CommonAudioErrorCodes.INIT_TIMEOUT) {
    if (yield select(AudioUtils.hasAudioInitCountExceededMax)) {
      yield put(
        D.Actions.UseCases.openErrorToast({
          ...PlaybackConstants.PlaybackMessages.havingTroubleStartingPlayback,
          messageName: D.Constants.Analytics.UserMessages.PLAYBACK_FAILURE,
        }),
      );
      yield put(AudioActions.trackEndError({ isRecoverable: false, sourceId, token }));
      log.warn('INIT_TIMEOUT error exceeds max threshold', payload);
      return;
    }
    log.trace('recovering from INIT_TIMEOUT error');
    yield put(D.Actions.Playback.reloadAudioPlaybackLibrary());
    return;
  }

  if (AudioConstants.RecoverableErrors.includes(error.commonType)) {
    if (
      resetBufferErrors.includes(error.commonType) ||
      AudioConstants.AudioHarnessName.SXM_AUDIO_PLAYER !== harnessName
    ) {
      // Delete the audio provider, so that if we somehow end up trying to load the same url we wont reuse the errored out provider.
      try {
        yield call(player.deleteProvider, url);
      } catch (e) {
        log.error(`failed to delete provider while handling ${error.commonType} error: ${e}`);
      }
    }

    if (
      Math.max(notSupportedSrcErrorCount, decodeErrorCount, networkErrorCount) >
      AudioConstants.MAX_AUDIO_RETRIES
    ) {
      yield call(handleNotifyTooManyPlaybackErrors);
      yield put(AudioActions.trackEndError({ isRecoverable: false, sourceId, token }));
      log.warn('Playback error exceeds max retries', {
        payload,
        decodeErrorCount,
        networkErrorCount,
        notSupportedSrcErrorCount,
      });
      return;
    }

    log.trace(`recovering from ${error.commonType} error`);
    // Audio url renewal is only available for APSItem (on-demand music).
    // The station playback url is from legacy API and it expires in an hour.
    if (
      harnessName &&
      trackModelType === 'APSItem' &&
      [
        AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
        AudioConstants.CommonAudioErrorCodes.NETWORK,
      ].includes(error.commonType)
    ) {
      const oldTrack = yield select(D.getInDomain('playback.selectors.getCurrentItemAudioTrack'));
      yield call(D.putAndWait, D.getInDomain('playback.actions.refreshCurrentItem')());
      const newTrack = yield select(D.getInDomain('playback.selectors.getCurrentItemAudioTrack'));
      if (oldTrack.get('trackToken') === newTrack.get('trackToken')) {
        const oldUrl = oldTrack.get('audioURL');
        const newUrl = newTrack.get('audioURL');
        if (oldUrl === newUrl) {
          // eslint-disable-next-line max-depth
          if (D.Utils.SourceType.isPodcastEpisodePandoraId(token)) {
            yield put(D.Actions.Modal.openPodcastEpisodeUnavailable());
          } else {
            yield put(D.Actions.Modal.openServiceErrorToast());
          }

          const sourceFrom = D.Utils.AudioPlayerUtils.getAudioContentHost(newUrl);
          // eslint-disable-next-line max-depth
          if (sourceFrom === AudioConstants.AudioContentHost.INTERNAL) {
            log.warn(`Failed to update internal url from ${oldUrl} to ${newUrl}`, payload);
          } else {
            log.warn(
              `Failed to update third-party url from ${oldUrl} to ${newUrl}. Please notify Content team`,
              payload,
            );
          }
          return;
        }

        try {
          yield call(player.updateUrl, oldUrl, newUrl);
          yield put(D.Actions.AudioPlayer.handlePostUrlUpdate(newUrl, newTrack));
        } catch (e) {
          // not all player instances allow a URL update, so we'll just move on with creating a new one
          log.warn(`Failed to update expired url from ${oldUrl} to ${newUrl} with error: ${e}`);
          yield put(D.getInDomain('playback.actions.resumeSource')());
        }
        return;
      }
    }
    yield put(AudioActions.trackEndError({ isRecoverable: true, sourceId, token }));
  } else {
    yield put(AudioActions.trackEndError({ isRecoverable: false, sourceId, token }));
    if (error.commonType === AudioConstants.CommonAudioErrorCodes.SERVICE_ERROR) {
      yield put(D.Actions.Modal.openServiceErrorToast());
    }
    log.warn('Non-recoverable error occurs', payload);
  }
}

export function* reportTrackCompleteAnalytics(action) {
  const baseData = yield select(getBaseAudioEventData, action);
  yield put(D.getInDomain('analytics.actions.createAudioTrackPlaybackEvent', noop)(baseData));
}

export function getBaseAudioEventData(
  state,
  { type: actionType, payload },
  eventTypeString = 'type', // 'type' | 'error'
) {
  // We get the track, duration, and currentTime from the action bc:
  // 1) When getting it from the store there are times when this data could be stale (e.g. `onTrackEnd`).
  // 2) When getting it from the track object there are times the track object will not have these values
  const { duration, harnessName } = payload;
  let { track, currentTime } = payload;
  const eventType = eventTypeString === 'error' ? payload.ERROR_OBJ.commonType : actionType;
  let percentLoaded = null;
  let audioToken = null;
  let audioEncoding = null;

  if (duration > 0) {
    const buffered = AudioUtils.getCurrentBufferedAmount(state);
    percentLoaded = 100.0 * (buffered / duration);

    if (duration && currentTime > duration) {
      currentTime = duration;
    }
  }

  if (!track) {
    // There are times the action will have a `token` instead of the `track` object (e.g. audio errors)
    const { token } = payload;
    if (token) {
      track = D.Utils.Playlist.getTrackById(state, token);
    }
  }

  if (track) {
    audioToken = D.Utils.Playlist.getTrackAudioToken(track);
    audioEncoding = D.Utils.Playlist.getTrackAudioEncoding(track);
  }

  const player = AudioUtils.AudioLoader.getPlayer() || {};
  const usingFlash = getIn(player, 'audioPlaybackState.options.tryForceFlash', null);

  return {
    audio_format: audioEncoding,
    audio_token_id: audioToken,
    current_time: currentTime,
    event_type: eventType,
    harness_name: harnessName,
    percent_loaded: percentLoaded,
    player_type: usingFlash ? 'flash' : 'html5',
    track_length: duration,
  };
}

export function* onPostAudioUpdate() {
  const bandwidthErrors = yield select(AudioUtils.getNumberOfBandwidthErrors);
  if (bandwidthErrors === AudioConstants.MAX_BANDWIDTH_WARNINGS) {
    yield put(
      D.Actions.UseCases.openErrorToast({
        ...PlaybackConstants.PlaybackMessages.bandwidthInsufficient,
        messageName: D.Constants.Analytics.UserMessages.BANDWIDTH_INSUFFICIENT,
      }),
    );
  }
}

/*
 * Watch for UPDATE action and execute blocking onPostAudioUpdate workflow
 * throttled to 500ms
 */
export function* onUpdate({ payload }) {
  yield D.tryCall(onPostAudioUpdate);
  yield delay(500);

  // Add 3 sec buffer to the check
  if (payload.AUDIO_CURRENT_TIME > payload.AUDIO_DURATION + 3) {
    const trackData = yield select(AudioUtils.getTrackDataForLogging);
    trackData.currentTime = payload.AUDIO_CURRENT_TIME;
    log.warn('Playhead has exceeded the track duration', trackData);
  }
}
